/**
 * The ground layer turned with the camera (render/camera.ts, render/turnView.ts). Between the
 * quarter turns the ground is painted for, the whole ground layer (the floor or the sky-side ground,
 * its shimmer, the marks on it, scenery shadows, ground effects) is drawn as at the quarter turn and
 * turned on screen as one picture about the map's middle, which is exact for a flat ground.
 *
 * The frame's offsets and size are widened for the layer to the part of the unturned ground that
 * lands on screen, so every pass under it fills and culls as usual; the sky stays unturned.
 *
 * Outdoors the painted ground itself is not turned by the canvas transform: its chunks are put in
 * the world picture as quads with a shader that snaps every pixel to the art grid and keeps the
 * commonest of four painted texels round it, on the GPU (render/turned/groundGpu.ts); the passes
 * after it (glints, shadows, marks, effects) go through the transform as before.
 */
import { pitchBand, pitchGroundRows } from '../pitchView';
import { GroundGpu } from '../turned/groundGpu';
import type { PassFrame } from './registry';
import { registerRenderPass } from './registry';

type Saved = { ox: number; oy: number; W: number; H: number; g: CanvasRenderingContext2D };
type Mutable = { -readonly [K in 'ox' | 'oy' | 'W' | 'H']: number } & { g: CanvasRenderingContext2D };
/** Each renderer's frame offsets and size from before the layer was turned, until it ends. */
const turned = new WeakMap<object, Saved>();
/** The ground being drawn turned on the GPU this frame: the turn and the box's offset. */
const grounds = new WeakMap<
  object,
  { m: [number, number, number, number, number, number]; x0: number; y0: number; gg: GroundGpu }
>();
const gpuGrounds = new WeakMap<object, GroundGpu>();

function begin(f: PassFrame, gpuGround = false): void {
  const iso = f.iso;
  if (!iso.warped || turned.has(f.renderer)) return;
  const [a, b, c, d, e, h] = iso.groundTransform(f.ox, f.oy);
  // The screen's corners, back on the unturned picture: the box round them is what to draw.
  const det = a * d - b * c;
  const back = (x: number, y: number): [number, number] => [
    (d * (x - e) - c * (y - h)) / det,
    (a * (y - h) - b * (x - e)) / det,
  ];
  const xs: number[] = [];
  const ys: number[] = [];
  // Lane CAMERA-PITCH: at a low pitch only the rows from the horizon to the near ground are drawn (pitchView.ts).
  const band = f.outdoors ? pitchBand(iso, iso.depth(f.world.player.x, f.world.player.y), f.player[1], f.oy, f.H) : null;
  const [r0, r1] = band ? pitchGroundRows(band, f.player[1], f.oy, f.H) : [0, f.H];
  for (const [x, y] of [
    [0, r0],
    [f.W, r0],
    [0, r1],
    [f.W, r1],
  ] as const) {
    const [u, v] = back(x, y);
    xs.push(u);
    ys.push(v);
  }
  const x0 = Math.floor(Math.min(...xs)) - 2;
  const y0 = Math.floor(Math.min(...ys)) - 2;
  const x1 = Math.ceil(Math.max(...xs)) + 2;
  const y1 = Math.ceil(Math.max(...ys)) + 2;
  turned.set(f.renderer, { ox: f.ox, oy: f.oy, W: f.W, H: f.H, g: f.g });
  const m = f as unknown as Mutable;
  if (gpuGround) {
    // The ground pass's chunks are only noted here and put in turned at order 125 (see the note).
    let gg = gpuGrounds.get(f.renderer);
    if (!gg) gpuGrounds.set(f.renderer, (gg = new GroundGpu()));
    gg.begin();
    grounds.set(f.renderer, { m: [a, b, c, d, e, h], x0, y0, gg });
    m.g = gg.recorder;
  } else {
    f.g.save();
    f.g.transform(a, b, c, d, e, h);
    f.g.translate(x0, y0);
  }
  m.ox = f.ox - x0;
  m.oy = f.oy - y0;
  m.W = x1 - x0;
  m.H = y1 - y0;
  iso.flat = true;
}

// Indoors before the room's floor (its dark fills the widened picture); outdoors after the sky.
registerRenderPass({
  id: 'turnFloor',
  layer: 'ground',
  order: 95,
  draw(f) {
    if (f.scene) begin(f);
  },
});

registerRenderPass({
  id: 'turnGround',
  layer: 'ground',
  order: 115,
  draw(f) {
    if (!f.scene) begin(f, true);
  },
});

// After the ground (120), before its glints (130, turned with the marks): its chunks turned on the
// GPU, then the canvas turned for the rest of the layer.
registerRenderPass({
  id: 'turnGroundSample',
  layer: 'ground',
  order: 125,
  draw(f) {
    const job = grounds.get(f.renderer);
    const saved = turned.get(f.renderer);
    if (!job || !saved) return;
    grounds.delete(f.renderer);
    const g = saved.g;
    job.gg.draw(f.gpu.world, g.getTransform(), job.m, job.x0, job.y0);
    (f as unknown as Mutable).g = g;
    g.save();
    g.transform(...job.m);
    g.translate(job.x0, job.y0);
  },
});

registerRenderPass({
  id: 'turnGroundEnd',
  layer: 'ground',
  order: 900,
  draw(f) {
    f.iso.flat = false;
    const saved = turned.get(f.renderer);
    if (!saved) return;
    turned.delete(f.renderer);
    // A GPU ground that never ran (no ground pass): the canvas was never turned.
    if (grounds.delete(f.renderer)) (f as unknown as Mutable).g = saved.g;
    else saved.g.restore();
    Object.assign(f as unknown as Mutable, saved);
  },
});
